
import React from 'react';
import { Interview } from '@/types/interview';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import InterviewEditor from './InterviewEditor';
import { EnhancedDialogTitle } from '@/lib/shadcn-enhanced';
import { useViewInterviewDialog } from '@/hooks/interview/useViewInterviewDialog';
import InterviewDialogContent from './dialog/InterviewDialogContent';
import InterviewDialogActions from './dialog/InterviewDialogActions';
import ConfirmationDialog from './ConfirmationDialog';

interface ViewInterviewDialogProps {
  interview: Interview | null;
  isOpen: boolean;
  onClose: () => void;
  onPublish: (interviewId: string) => Promise<void>;
  onSendToFeeder: (interviewId: string) => Promise<void>;
  publishingId: string | null;
  sendingToFeederId: string | null;
}

const ViewInterviewDialog: React.FC<ViewInterviewDialogProps> = ({
  interview,
  isOpen,
  onClose,
  onPublish,
  onSendToFeeder,
  publishingId,
  sendingToFeederId
}) => {
  const {
    error,
    dialogKey,
    photoUrl,
    isPublishConfirmOpen,
    isFeederConfirmOpen,
    setIsPublishConfirmOpen,
    setIsFeederConfirmOpen,
    handleSendToFeeder,
    handlePublish,
    isSendingToFeeder,
    isPublishing,
    isDisabled
  } = useViewInterviewDialog(
    interview,
    isOpen,
    onPublish,
    onSendToFeeder,
    publishingId,
    sendingToFeederId
  );

  // Return null if no interview
  if (!interview) {
    return null;
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <EnhancedDialogTitle>Forhåndsvisning</EnhancedDialogTitle>
          </DialogHeader>
          
          <InterviewDialogContent 
            interview={interview} 
            photoUrl={photoUrl}
            dialogKey={dialogKey}
            error={error}
          />

          <InterviewDialogActions
            onClose={onClose}
            onOpenPublishConfirm={() => setIsPublishConfirmOpen(true)}
            onOpenFeederConfirm={() => setIsFeederConfirmOpen(true)}
            isInFeeder={!!interview.is_in_feeder}
            isPublishing={isPublishing}
            isSendingToFeeder={isSendingToFeeder}
            isDisabled={isDisabled}
            interview={interview}
          />
        </DialogContent>
      </Dialog>

      {/* Confirmation dialogs */}
      <ConfirmationDialog
        isOpen={isPublishConfirmOpen}
        onClose={() => setIsPublishConfirmOpen(false)}
        onConfirm={async () => {
          try {
            await handlePublish();
            setIsPublishConfirmOpen(false);
          } catch (error) {
            console.error('Error in publish confirmation:', error);
          }
        }}
        title="Bekreft publisering"
        description="Er du sikker på at du vil publisere dette intervjuet?"
        confirmText="Ja, publiser"
        cancelText="Avbryt"
      />

      <ConfirmationDialog
        isOpen={isFeederConfirmOpen}
        onClose={() => setIsFeederConfirmOpen(false)}
        onConfirm={async () => {
          try {
            await handleSendToFeeder();
            setIsFeederConfirmOpen(false);
          } catch (error) {
            console.error('Error in send to feeder confirmation:', error);
          }
        }}
        title="Bekreft sending til køen"
        description="Er du sikker på at du vil sende dette intervjuet til køen?"
        confirmText="Ja, send til køen"
        cancelText="Avbryt"
      />
    </>
  );
};

export default ViewInterviewDialog;
